import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  "Drilling",
  "Manufacturing",
  "Fabrication",
  "Safety",
  "Mining",
  "Equipment",
  "Oil & Gas",
  "Other",
];

const initial = {
  business_name: "",
  industry: "",
  product_type: "",
  name: "",
  phone: "",
  email: "",
};

export const PartnerInquiryForm = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (e) => {
    const { name, value } = e.target;
    if (name === "product_type" && value.length > 200) return;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.industry || !form.product_type || !form.name || !form.email) {
      toast.error("Please fill in business name, industry, product type, name, and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/partner-inquiry`, form);
      toast.success("Thank you. Your partnership inquiry has been received — we'll be in touch.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please email adam@slatechsolutions.com directly.");
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    "w-full bg-transparent border border-zinc-700 focus:border-blue-500 outline-none px-4 py-3.5 text-white placeholder:text-zinc-500 transition-colors duration-200 font-sans";
  const labelClass =
    "block text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-2";

  return (
    <form data-testid="partner-form" onSubmit={submit} className="space-y-5">
      <div>
        <label className={labelClass}>Business Name *</label>
        <input
          data-testid="partner-business-name"
          name="business_name"
          value={form.business_name}
          onChange={update}
          placeholder="Your business name"
          className={fieldClass}
        />
      </div>

      <div>
        <label className={labelClass}>Industry *</label>
        <select
          data-testid="partner-industry"
          name="industry"
          value={form.industry}
          onChange={update}
          className={`${fieldClass} appearance-none cursor-pointer ${form.industry ? "text-white" : "text-zinc-500"}`}
        >
          <option value="" disabled>Select an industry</option>
          {INDUSTRIES.map((i) => (
            <option key={i} value={i} className="bg-zinc-900 text-white">
              {i}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className={labelClass}>Product Type *</label>
        <textarea
          data-testid="partner-product-type"
          name="product_type"
          value={form.product_type}
          onChange={update}
          rows={4}
          maxLength={200}
          placeholder="Briefly describe the products or services you offer"
          className={`${fieldClass} resize-none`}
        />
        <p className="text-right text-xs text-zinc-500 mt-1">{form.product_type.length}/200</p>
      </div>

      <div className="pt-2">
        <label className={labelClass}>Contact Info</label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <input
            data-testid="partner-name"
            name="name"
            value={form.name}
            onChange={update}
            placeholder="Name *"
            className={fieldClass}
          />
          <input
            data-testid="partner-phone"
            name="phone"
            value={form.phone}
            onChange={update}
            placeholder="Phone"
            className={fieldClass}
          />
          <input
            data-testid="partner-email"
            name="email"
            type="email"
            value={form.email}
            onChange={update}
            placeholder="Email *"
            className={fieldClass}
          />
        </div>
      </div>

      <button
        data-testid="partner-submit"
        type="submit"
        disabled={loading}
        className="inline-flex items-center justify-center gap-2.5 font-heading text-base font-semibold uppercase tracking-widest bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-8 py-4 transition-colors duration-200 w-full sm:w-auto"
      >
        {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        {loading ? "Sending..." : "Send Partnership Inquiry"}
      </button>
    </form>
  );
};
